import * as React from "react";
import { X } from "lucide-react";
import { useLocale } from "@/contexts/LocaleContext";
import { MARKETS } from "@/lib/markets";

/**
 * بانر علويّ ذكيّ بنمطين:
 * 1) **Mismatch** (أولويّة): IP يكشف بلداً ≠ السوق المختار حالياً → تحذير
 *    "نظنّك في {country} — الأسعار أفضل بـ {currency}" + زرّ تبديل بنقرة واحدة.
 *    حسّاس تجاريّاً لأنّ سعر ROW أعلى من المحليّ بشكل ملحوظ.
 * 2) **Detection**: الكشف الأوّل بدون تعارض → إشعار "Prices in {currency} based
 *    on your location" يُغلق آلياً.
 */
export function CountryDetectionBanner() {
  const {
    showDetectionBanner,
    dismissDetectionBanner,
    showMismatchBanner,
    dismissMismatchBanner,
    detectedMarketId,
    marketId,
    locale,
    currency,
    setMarket,
  } = useLocale();

  const isMismatch = showMismatchBanner;

  // ⏳ تأخير ٣ ثوانٍ قبل الظهور حتى لا يغطّي البانر الهيرو في أوّل انطباع.
  const [delayPassed, setDelayPassed] = React.useState(false);
  React.useEffect(() => {
    const id = window.setTimeout(() => setDelayPassed(true), 3000);
    return () => window.clearTimeout(id);
  }, []);

  React.useEffect(() => {
    if (isMismatch) return; // mismatch لا يُغلق آلياً — قرار تجاريّ
    if (!showDetectionBanner || !delayPassed) return;
    const id = window.setTimeout(
      dismissDetectionBanner,
      marketId === "ROW" ? 14000 : 8000,
    );
    return () => window.clearTimeout(id);
  }, [showDetectionBanner, isMismatch, dismissDetectionBanner, marketId, delayPassed]);

  if (!delayPassed) return null;
  if (!isMismatch && !showDetectionBanner) return null;


  // ─── حالة Mismatch ───
  if (isMismatch && detectedMarketId) {
    const detected = MARKETS[detectedMarketId];
    const detectedCountry = detected.labels[locale];
    const message =
      locale === "ar"
        ? `يبدو أنّك في ${detectedCountry} — الأسعار أفضل بـ ${detected.currency} (السعر الحاليّ بـ ${currency} للأسواق الأخرى).`
        : `Looks like you're in ${detectedCountry} — prices are better in ${detected.currency} (showing ${currency} for other markets).`;
    const switchLabel =
      locale === "ar"
        ? `بدّل إلى ${detected.currency}`
        : `Switch to ${detected.currency}`;

    return (
      <div
        role="alert"
        className="fixed inset-x-3 top-24 z-40 mx-auto flex max-w-3xl items-start justify-between gap-3 rounded-none border border-amber-500/50 bg-background px-4 py-2.5 text-xs text-foreground  animate-fade-up md:top-[116px] md:items-center"
      >
        <span className="flex items-center gap-2">
          <span className="text-base leading-none">{detected.flag}</span>
          <span className="opacity-90 leading-relaxed">{message}</span>
        </span>
        <span className="flex items-center gap-2">
          <button
            onClick={() => {
              setMarket(detectedMarketId);
              dismissMismatchBanner();
            }}
            className="rounded border border-primary/60 bg-primary px-2.5 py-1.5 t-micro uppercase tracking-[var(--tracking-wider)] text-primary-foreground transition-colors hover:opacity-90"
          >
            {switchLabel}
          </button>
          <button
            onClick={dismissMismatchBanner}
            aria-label="Dismiss"
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </span>
      </div>
    );
  }

  // ─── حالة Detection (السلوك السابق) ───
  const market = MARKETS[marketId];
  const country = market.labels[locale];
  const isRow = marketId === "ROW";

  const message = isRow
    ? locale === "ar"
      ? `نخدم حالياً السعودية · الإمارات · العراق · عُمان · الولايات المتحدة · البرازيل. الأسعار بالدولار للاطّلاع.`
      : `We currently ship to Saudi Arabia · UAE · Iraq · Oman · United States · Brazil. Prices in USD shown for reference.`
    : locale === "ar"
      ? `الأسعار معروضة بـ ${currency} بناءً على موقعك (${country}).`
      : `Prices shown in ${currency} based on your location (${country}).`;

  const changeLabel = locale === "ar" ? "تغيير الدولة" : "Change country";

  const scrollToHeaderSwitcher = () => {
    dismissDetectionBanner();
    const el = document.querySelector<HTMLButtonElement>("[data-market-chip]");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.focus();
      el.click();
    }
  };

  return (
    <div
      role="status"
      className={
        "fixed inset-x-3 top-24 z-40 mx-auto flex max-w-3xl items-start justify-between gap-2 rounded-none border bg-background px-3 py-2 text-[11px] text-foreground animate-fade-up sm:gap-3 sm:px-4 sm:py-2.5 sm:text-xs md:top-[116px] md:items-center " +
        (isRow ? "border-amber-500/40" : "border-primary/30")
      }
    >
      <span className="flex min-w-0 items-start gap-2 sm:items-center">
        <span className="shrink-0 text-sm leading-none sm:text-base">{market.flag}</span>
        <span className="min-w-0 opacity-90 leading-relaxed">{message}</span>
      </span>
      <span className="flex shrink-0 items-center gap-1.5 sm:gap-2">
        <button
          onClick={scrollToHeaderSwitcher}
          className="min-h-[36px] whitespace-nowrap rounded border border-primary/50 px-2 py-2.5 text-[10px] uppercase tracking-[var(--tracking-wider)] text-primary transition-colors hover:bg-primary hover:text-primary-foreground sm:text-[11px]"
        >
          {changeLabel}
        </button>
        <button
          onClick={dismissDetectionBanner}
          aria-label="Dismiss"
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </span>
    </div>
  );
}
